import styles from './index.module.css'
import api from '@disco/api/server'
import {
  getSubscriptionPeriod,
  getSubscriptionCurrentAmount,
} from '@disco/state-management/selectors/subscription'
import {
  getTracksLimits,
  getUser,
  getUsersLimits,
} from '@disco/state-management/selectors/user'
import { UNLIMITED_WATERMARKING_QUOTA } from '@disco/utils/getWatermarkingQuota'
import {
  ADDON_DISCOVERY_SUITE,
  ADDON_WATERMARKING,
  ADDON_STORAGE,
  ADDON_SEATS,
} from '@disco/constants/billing-provider-components'
import { loadBillingProviderComponents } from '@disco/state-management/actions/billing-provider-components'

import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import { formatCurrency, pricingPlans } from '@disco/utils/pricing'
import cn from 'clsx'
import captureError from '@disco/utils/captureError'
import {
  getChargebeeAddon,
  chargebeePlanPriceLookup,
} from '@disco/utils/chargebee'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { Link, useNavigate } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import CardNumber from 'components/common/card-number'
import { dataTables } from 'components/common/pricing2021/feature-table/tables-data'
import Price from 'components/common/pricing2021/pricing-table/pricing-plan/price'
import BillingContactInformation from 'components/common/subscribed-user-billing/billing-contact-information'
import DialogFooter from 'components/modals/dialog-footer'
import ActionRow from 'components/ui/action-row'
import Button from 'components/ui/button'
import Card from 'components/ui/card'
import Checkbox from 'components/ui/checkbox'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import List from 'components/ui/list'
import ListItemWithIcon from 'components/ui/list-item-with-icon'
import Spinner from 'components/ui/spinner'
import Stack from 'components/ui/stack'
import StaffOnly from 'components/ui/staff-only'
import Text from 'components/ui/text'
import VerticalSpacer from 'components/ui/vertical-spacer'
import { Box, Text as DsText } from 'components/ds'
import { SubscriptionDisclaimerText } from '../SubscriptionDisclaimerText'

type Props = {
  children: React.ReactNode
  location: Location
}

function formatWMLabel(numberOfPlaylists) {
  // TODO: move to utils, there's at least one duplicate implementation of formatWMLabel
  if (numberOfPlaylists === 1) return `1 playlist`
  if (numberOfPlaylists === UNLIMITED_WATERMARKING_QUOTA)
    return `Unlimited playlists`
  return `${numberOfPlaylists} playlists`
}

type PreviousPlan = {
  name: string
  handle: string
  price: string
  period: 'monthly' | 'annual' | null
  users: string
  tracks: string
  components: Array<{
    component_id: string
    allocated_quantity: 'unlimited' | number
  }>
}

type NewPlan = {
  name: string
  handle: string
  price: number
  period: string
  users: number
  tracks: number
  watermarking: 'unlimited' | number
  discovery_suite: number
  components: Array<{
    component_id: string
    allocated_quantity: 'unlimited' | number
  }>
}

type ChargebeePlan = {
  id: string
  plan_id: string
  period_unit: 'month' | 'year'
}

const PlanChangeSummary = (props: Props): React.ReactNode => {
  //get data for the previous plan
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const usersLimits = useSelector(getUsersLimits)
  const tracksLimits = useSelector(getTracksLimits)
  const subscription = useSelector(state => state.subscription)
  const subscriptionPeriod = useSelector(getSubscriptionPeriod)
  const subscriptionAmount = useSelector(getSubscriptionCurrentAmount)
  const pricingForm = useSelector(state => state.forms.pricingForm)
  const billingProviderComponents = useSelector(
    state => state.billingProviderComponents,
  )
  const userData = useSelector(getUser)
  const { children, location } = props
  const [chargebeeData, setChargebeeData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [changing, setChanging] = useState(false)
  const [isUpgrade, setIsUpgrade] = useState(null)
  const [acceptLoss, setAcceptLoss] = useState(false)
  const queryParams = new URLSearchParams(location.search)
  const planHandle = pricingForm.plan?.handle || queryParams.get('handle')
  const activePlan = pricingPlans.find(plan => plan.handle === planHandle)
  const planColour = activePlan?.chosenColor.line.backgroundColor
  const prevPlan_: PreviousPlan = {
    name: userData.business.private_metadata.plan?.name,
    handle: userData.business.private_metadata.plan?.id,
    price: subscriptionAmount,
    period: subscriptionPeriod,
    users: usersLimits.maxAsDisplayString,
    tracks: tracksLimits.maxAsDisplayString,
    components: [],
  }

  // prioritize pricingForm, use queryParams as fallback
  const getRehydratedState = (): NewPlan => {
    return {
      name: pricingForm?.plan?.name ?? activePlan?.name,
      handle: pricingForm?.plan?.handle ?? queryParams.get('handle'),
      price: 0,
      period: pricingForm?.paymentPeriod ?? queryParams.get('period'),
      users:
        pricingForm?.componentsQuantity?.users ??
        Number(queryParams.get('users')),
      tracks:
        pricingForm?.componentsQuantity?.tracks ??
        Number(queryParams.get('tracks')),
      watermarking:
        pricingForm?.componentsQuantity?.watermarking ??
        (queryParams.get('watermarking') as 'unlimited' | number),
      discovery_suite:
        pricingForm?.componentsQuantity?.discoverySuite ??
        Number(queryParams.get('discovery_suite')),
      components: [],
    }
  }

  const [newPlan_, setNewPlan] = useState<NewPlan>(getRehydratedState())
  // Effect to rehydrate newPlan on page refresh
  dispatch(loadBillingProviderComponents())
  useEffect(() => {
    const rehydratedPlan = getRehydratedState()
    // Update state only if there's a difference to prevent unnecessary renders
    setNewPlan(prevPlan =>
      JSON.stringify(prevPlan) === JSON.stringify(rehydratedPlan)
        ? prevPlan
        : rehydratedPlan,
    )
  }, [pricingForm, queryParams.toString()])
  const planHierarchy = {
    '2020-plus': 1,
    '2020-pro': 2,
    '2020-enterprise': 3,
  }

  const determineUpgrade = (prevPlanHandle, newPlanHandle) => {
    const currentRank = planHierarchy[prevPlanHandle]
    const targetRank = planHierarchy[newPlanHandle]
    return currentRank < targetRank ? setIsUpgrade(true) : setIsUpgrade(false)
  }

  let lostFeatures = []

  const getLostFeatures = (currentPlan, targetPlan) => {
    dataTables.forEach(table => {
      table.data.forEach(item => {
        const currentFeature = item[currentPlan]
        const targetFeature = item[targetPlan]

        if (currentFeature && !targetFeature) {
          lostFeatures.push(item.feature.name)
        }
      })
    })
    return lostFeatures
  }

  const handleCheckboxChange = () => {
    setAcceptLoss(!acceptLoss)
  }

  function getChargebeePlansForRender(newPlan_, prevPlan_) {
    const baseQuota = { [ADDON_STORAGE]: 1000, [ADDON_SEATS]: 1 } // TODO: remove hardcoding
    // sending target configuration, diffing will happen on Chargebee side (prevPlan vs newPlan)
    return {
      prevPlan: prevPlan_,
      newPlan: {
        ...newPlan_,
        price: formatCurrency(
          chargebeeData?.chargebeePlanChange.recurring_estimate.total,
        ),
        addons: [
          {
            id: ADDON_WATERMARKING,
            quantity: newPlan_.watermarking,
          },
          {
            id: ADDON_DISCOVERY_SUITE,
            quantity: newPlan_.discovery_suite,
          },
          {
            id: ADDON_STORAGE,
            quantity: newPlan_.tracks - baseQuota[ADDON_STORAGE],
          },
          {
            id: ADDON_SEATS,
            quantity: newPlan_.users - baseQuota[ADDON_SEATS],
          },
        ],
      },
    }
  }
  const { newPlan, prevPlan } = getChargebeePlansForRender(newPlan_, prevPlan_)

  async function chargebeeSummary(billingProviderComponents) {
    setLoading(true)
    try {
      if (!billingProviderComponents.loaded) return // TODO: rework existing loading sequence
      const newPlanInChargebee = chargebeePlanPriceLookup(
        billingProviderComponents.chargebeePlans,
        newPlan.handle,
        newPlan.period,
      )
      const addons = newPlan.addons.flatMap(a =>
        getChargebeeAddon({
          type: a.id,
          allocated_quantity: a.quantity,
          allAddons: billingProviderComponents.addons,
        }),
      )
      const args = {
        plan_id: newPlanInChargebee.id,
        addons,
      }
      const data = {
        preview: {},
        componentsToRemove: [],
        chargebeePlanChangeArguments: args,
        chargebeePlanChange: await api.chargebeeEstimatePlanChange(args),
        componentsToAdd: [],
      }
      setChargebeeData(data)
      setLoading(false)
    } catch (err) {
      // TODO: add error handling UI state
      console.log('err', err)
      setLoading(false)
    }
  }

  async function onSubmit(operations) {
    setChanging(true)

    try {
      const results = []

      for (let operation of operations) {
        let result

        if (operation.chargebeePlanChange) {
          result = await api.chargebeePerformPlanChange(
            operation.chargebeePlanChange,
          )
        }

        results.push({
          operation,
          result,
        })
      }

      console.log('onSubmit result', results)
      navigate('/settings/billing')
    } catch (error) {
      console.error('onSubmit error', error)
      const errorStr = parseServerError(error.body)
      pushUINotification({
        title: 'Payment processing error',
        message: `An error occurred while changing to the ${
          newPlan.name
        } plan. Please try again later or |${JSON.stringify({
          contactUsLink: 'contact our support team',
        })}| for assistance.\n\nError: ${errorStr}`,
        level: 'error',
        dismissible: 'button',
      })
      captureError(error, { description: 'Error in plan change onSubmit' })
    }

    setChanging(false)
  }

  useEffect(() => {
    determineUpgrade(prevPlan.handle, newPlan.handle)
    chargebeeSummary(billingProviderComponents)
  }, [newPlan.handle, prevPlan.handle, billingProviderComponents])

  if (loading) {
    return (
      <div>
        <Spinner />
      </div>
    )
  }

  if (!isUpgrade) {
    getLostFeatures(prevPlan.handle, newPlan.handle)
  }

  const disabledUpdatePaymentDetails =
    !userData.permissions?.includes('can_change_business_settings') ||
    !userData.business?.chargebee_subscription_id
  const showUpdatePaymentDetails = Boolean(
    userData.business?.chargebee_subscription_id,
  )
  const cardEnding = subscription.chargebee?.last_segment_of_card_number
  const updateBillingInfoButton = showUpdatePaymentDetails && (
    <div className="SubscribedUserBilling_Tooltip">
      {disabledUpdatePaymentDetails ? (
        <span className="SubscribedUserBilling_ContactUsLink SubscribedUserBilling_ContactUsLink--Disabled">
          Update payment method
        </span>
      ) : (
        <Link
          to="/settings/billing/summary/update"
          state={{
            returnTo: props.location.pathname,
          }}
          className="SubscribedUserBilling_ContactUsLink"
        >
          Update payment method
        </Link>
      )}
    </div>
  )

  const pricingPlanTitle = (plan: NewPlan | PreviousPlan) => (
    <>
      <div className={styles.PlanChangeSummary_TitleWrapper}>
        <div className={styles.PlanChangeSummary_TitleContainer}>
          <span className={styles.PlanChangeSummary_Title}>{plan.name}</span>
        </div>
        <div className={styles.PlanChangeSummary_PriceContainer}>
          <Price
            currencySymbol={typeof plan.price === 'number' ? '$' : null}
            price={<>{plan.price}</>}
            period={plan.period === 'annual' ? '/year' : '/mo'}
          />
          <div className={styles.PlanChangeSummary_Period}>
            <span>
              Billed{' '}
              <span>{plan.period === 'annual' ? 'yearly' : plan.period}</span>
            </span>
          </div>
        </div>
      </div>
    </>
  )

  const planList = plan => (
    <List>
      <ListItemWithIcon iconNode={<Icon type="profiles" color="#6B778C" />}>
        {plan.users} users
      </ListItemWithIcon>
      <ListItemWithIcon iconNode={<Icon type="lyrics" color="#6B778C" />}>
        {plan.tracks} tracks
      </ListItemWithIcon>
    </List>
  )

  const details = {
    fee: 0,
    feeItems: [],
    credits: 0,
    creditsItems: [],
    billedToday: 0,
    WMPrice: 0,
    DSPrice: 0,
  }

  if (chargebeeData) {
    for (let c of chargebeeData.chargebeePlanChange.recurring_estimate
      .line_items) {
      let cost = c.amount_in_cents
      details.fee += cost
      if (c.component_handle)
        details.feeItems.push({
          amount: cost,
          memo: c.component_handle,
        })
      if (c.product_handle)
        details.feeItems.push({
          amount: cost,
          memo: c.product_handle,
        })
    }

    details.credits =
      chargebeeData.chargebeePlanChange.prorated_estimate.prorated_adjustment_in_cents
    details.fee = chargebeeData.chargebeePlanChange.recurring_estimate.total
    details.billedToday =
      chargebeeData.chargebeePlanChange.prorated_estimate.payment_due_in_cents
    details.DSPrice =
      chargebeeData.chargebeePlanChange.recurring_estimate.line_items.find(
        e => e.entitlement_id === ADDON_DISCOVERY_SUITE,
      )?.amount || 0
    details.WMPrice =
      chargebeeData.chargebeePlanChange.recurring_estimate.line_items.find(
        e => e.entitlement_id === ADDON_WATERMARKING,
      )?.amount || 0
  }

  const calculateCreditsApplied = (billAmount, availableCredits) => {
    return Math.min(billAmount, availableCredits)
  }

  const billAmount = details.fee
  const availableCredits = Math.abs(details.credits)
  const creditsApplied = calculateCreditsApplied(billAmount, availableCredits)
  const finalBillAmount = billAmount - creditsApplied
  const billingDetails = (
    <div className={styles.PlanChangeSummary_BillingDetails}>
      <Heading size={600}>Billing details</Heading>
      <div className={styles.PlanChangeSummary_BillingDetailsItem}>
        <div className={styles.PlanChangeSummary_BillingDetailsItemLeft}>
          {newPlan.name} Plan ({newPlan.users} users, {newPlan.tracks} tracks)
        </div>
        <div className={styles.PlanChangeSummary_BillingDetailsItemRight}>
          {formatCurrency(details.fee - details.DSPrice - details.WMPrice)}
        </div>
      </div>
      {newPlan.discovery_suite > 0 && (
        <div className={styles.PlanChangeSummary_BillingDetailsItem}>
          <div className={styles.PlanChangeSummary_BillingDetailsItemLeft}>
            Discovery Suite ({newPlan.discovery_suite} tracks)
          </div>
          <div className={styles.PlanChangeSummary_BillingDetailsItemRight}>
            {formatCurrency(details.DSPrice)}
          </div>
        </div>
      )}
      {typeof newPlan.watermarking === 'number' && newPlan.watermarking > 0 && (
        <div className={styles.PlanChangeSummary_BillingDetailsItem}>
          <div className={styles.PlanChangeSummary_BillingDetailsItemLeft}>
            Watermarking ({formatWMLabel(newPlan.watermarking)})
          </div>
          <div className={styles.PlanChangeSummary_BillingDetailsItemRight}>
            {formatCurrency(details.WMPrice)}
          </div>
        </div>
      )}
      <div
        className={cn(
          styles.PlanChangeSummary_BillingDetailsItem,
          styles.PlanChangeSummary_BillingDetailsItemLeft__black,
        )}
      >
        <div>Billed {pricingForm.paymentPeriod}</div>
        <div className={styles.PlanChangeSummary_BillingDetailsItemRight}>
          {formatCurrency(details.fee)}
          <StaffOnly
            explain={details.feeItems
              .map(c => `${formatCurrency(c.amount)} ${c.memo}`)
              .concat(`${formatCurrency(details.fee)} total`)
              .join('\n')}
          />
        </div>
      </div>
      {Math.abs(details.credits) > 0 && (
        <div className={styles.PlanChangeSummary_BillingDetailsItem}>
          <div className={styles.PlanChangeSummary_BillingDetailsItemLeft}>
            Credits balance
          </div>
          <div className={styles.PlanChangeSummary_BillingDetailsItemRight}>
            {formatCurrency(Math.abs(details.credits))}
            <StaffOnly
              explain={details.creditsItems
                .map(c => `${formatCurrency(c.amount)} ${c.memo}`)
                .concat(`${formatCurrency(details.credits)} total`)
                .join('\n')}
            />
          </div>
        </div>
      )}
      {Math.abs(details.credits) > 0 && (
        <div className={styles.PlanChangeSummary_BillingDetailsItem}>
          <div className={styles.PlanChangeSummary_BillingDetailsItemLeft}>
            Credits applied
          </div>
          <div className={styles.PlanChangeSummary_BillingDetailsItemRight}>
            {formatCurrency(Math.abs(creditsApplied))}
          </div>
        </div>
      )}
      <div
        className={cn(
          styles.PlanChangeSummary_BillingDetailsItem,
          styles.PlanChangeSummary_BillingDetailsItemLeft__black,
        )}
      >
        <div>Billed today</div>
        <div className={styles.PlanChangeSummary_BillingDetailsItemRight}>
          {formatCurrency(finalBillAmount)}
          <StaffOnly
            explain={[
              `${formatCurrency(details.credits)} credits`,
              `${formatCurrency(details.fee)} fee`,
              `${formatCurrency(details.billedToday)} total`,
            ].join('\n')}
          />
        </div>
      </div>
      <Box mt="5">
        <SubscriptionDisclaimerText subscriptionPeriod={subscriptionPeriod} />
      </Box>
    </div>
  )
  let operations
  if (chargebeeData) {
    operations = [
      {
        chargebeePlanChange: chargebeeData.chargebeePlanChangeArguments,
      },
    ]
  }
  const upgradeInfoText = (operations || [])
    .flatMap((op, idx) => {
      const step = `\n${idx + 1}. `

      if (op.allocations) {
        return [step + 'allocate components'].concat(
          op.allocations.map(
            a =>
              ` * ${a.quantity} of ${a.component_handle} (${a.component_id})`,
          ),
        )
      } else if (op.plan) {
        return (
          step +
          `migrate plan to ${op.plan.planHandle} ${op.plan.paymentPeriod}`
        )
      } else return step + JSON.stringify(op)
    })
    .join('\n')
  const upgradeInfo = <StaffOnly explain={upgradeInfoText} />
  return (
    <Stack space="small">
      <Heading size={700}>Confirm plan details</Heading>
      <div>
        <Card>
          <ActionRow
            plainStyle
            titleLeft="New plan"
            titleRight="Previous plan"
            leftContent={
              <>
                <Card billing planColour={planColour}>
                  {pricingPlanTitle(newPlan)}
                  {planList(newPlan)}
                </Card>
              </>
            }
            rightContent={
              <>
                <Card billing disabled>
                  {pricingPlanTitle(prevPlan)}
                  {planList(prevPlan)}
                </Card>
              </>
            }
          />
          {billingDetails}
        </Card>
      </div>

      <div className={styles.PlanChangeSummary_PaymentDetails}>
        {cardEnding && (
          <Card
            title="Payment method"
            footerLinks={[
              {
                linkNode: updateBillingInfoButton,
              },
            ]}
          >
            <CardNumber />
          </Card>
        )}
        {!disabledUpdatePaymentDetails &&
          Boolean(subscription?.chargebee?.subscription_id) && (
            <Card title="Billing contact information">
              <BillingContactInformation
                isPlanChange
                location={'/settings/billing/summary/'}
              />
            </Card>
          )}
      </div>

      {!isUpgrade ? (
        <div className={styles.PlanChangeSummary_ReducedFeaturesContainer}>
          <Card>
            <Heading size={600}>
              <Icon
                className={styles.PlanChangeSummary_ReducedFeaturesIcon}
                type="warning"
              />{' '}
              <span>Reduced features</span>
            </Heading>
            <VerticalSpacer />
            <Text type="paragraph" display="block">
              By switching to the {newPlan.name} Plan, you’ll lose access to the
              following features, which are enabled on your account:
            </Text>
            <ul>
              {lostFeatures.map(feature => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <Checkbox
              label="I understand that I’ll lose access to these features, and agree to the plan change"
              checked={acceptLoss}
              onChange={handleCheckboxChange}
            />
            <VerticalSpacer />
            <DialogFooter
              primary={
                <>
                  {upgradeInfo}
                  <Button
                    appearance="primary"
                    caption="Confirm new plan"
                    progressIndicator={changing}
                    progressCaption="Updating plan"
                    onClick={() => onSubmit(operations)}
                    disabled={!acceptLoss && !isUpgrade}
                  />
                </>
              }
              secondary={
                <Button
                  caption="Back to plan selection"
                  link="/settings/billing/plans"
                />
              }
            />
          </Card>
        </div>
      ) : (
        <Card>
          <DialogFooter
            primary={
              <>
                {upgradeInfo}
                <Button
                  appearance="primary"
                  caption="Confirm new plan"
                  progressIndicator={changing}
                  progressCaption="Updating plan"
                  onClick={() => onSubmit(operations)}
                />
              </>
            }
            secondary={
              <Button
                caption="Back to plan selection"
                link="/settings/billing/plans"
              />
            }
          />
        </Card>
      )}

      {children}
    </Stack>
  )
}

export default PlanChangeSummary
